export const clientsKeys = {
  all: () => ["clients"],
  archived: () => ["clients", "archived"],
  detail: (clientId) => ["clients", clientId],
};

export const brandsKeys = {
  all: () => ["brands", "all"],
  archived: () => ["brands", "archived"],
  byClient: (clientId) => ["brands", { clientId }],
  detail: (brandId) => ["brands", "detail", brandId],
};

export const projectsKeys = {
  all: () => ["projects", "all"],
  archived: () => ["projects", "archived"],
  count: () => ["projects", "count"],
  byClient: (clientId) => ["projects", { clientId }],
  byBrand: (brandId) => ["projects", { brandId }],
  detail: (projectId) => ["projects", "detail", projectId],
};

export const referenceDocumentsKeys = {
  all: () => ["reference-documents", "all"],
  archived: () => ["reference-documents", "archived"],
  count: () => ["reference-documents", "count"],
  byProject: (projectId) => ["reference-documents", { projectId }],
  detail: (referenceId) => ["reference-documents", "detail", referenceId],
  sourceRevision: (referenceId) => [
    "reference-documents",
    "source-revision",
    referenceId,
  ],
};

export const assetsKeys = {
  all: () => ["assets", "all"],
  archived: () => ["assets", "archived"],
  detail: (assetId) => ["assets", "detail", assetId],
  overlays: (assetId) => ["assets", "overlays", assetId],
  lifecycleImpact: (assetId, pageSize = 50) => [
    "assets",
    "lifecycle-impact",
    assetId,
    pageSize,
  ],
};

export const pinsKeys = {
  all: () => ["pins"],
  scope: (scope) => ["pins", scope],
};

export const searchKeys = {
  all: () => ["search-items"],
  scope: (scope) => ["search-items", scope],
};

export const userSettingsKeys = {
  all: (userId) => ["user-settings", userId],
};

export const dashboardKeys = {
  summary: ({ scope, teamId, orgId, userId, offset } = {}) => [
    "dashboard",
    "summary",
    {
      scope: scope || "all",
      teamId: teamId || null,
      orgId: orgId || null,
      userId: userId || null,
      offset: Number.isFinite(Number(offset)) ? Number(offset) : 0,
    },
  ],
};

export const sessionKeys = {
  context: () => ["session-context"],
};

export const configKeys = {
  all: () => ["config"],
  detail: (configKey) => ["config", configKey],
};

export const teamKeys = {
  memberCount: (teamId) => ["team", "member-count", teamId],
  members: (teamId) => ["team", "members", teamId],
  orgMembers: (orgId) => ["team", "org-members", orgId],
  orgTeams: (orgId) => ["team", "org-teams", orgId],
  orgSeatCount: (orgId) => ["team", "org-seat-count", orgId],
  context: (userId) => ["team", "context", userId],
  invites: (teamId) => ["team", "invites", teamId],
};

export const messageKeys = {
  inbox: (teamId) => ["messages", "inbox", teamId ?? "unknown"],
  allConversationMessages: () => ["messages", "conversation-messages"],
  conversationMessages: (conversationId) => [
    "messages",
    "conversation-messages",
    conversationId,
  ],
  conversationEntities: (conversationId) => [
    "messages",
    "conversation-entities",
    conversationId,
  ],
  conversationMembers: (conversationId) => [
    "messages",
    "conversation-members",
    conversationId,
  ],
  conversationSearch: (conversationId, query) => [
    "messages",
    "conversation-search",
    conversationId,
    query,
  ],
  inboxSearch: (teamId, query) => [
    "messages",
    "inbox-search",
    teamId ?? "unknown",
    query,
  ],
  tagCandidates: (teamId, query) => [
    "messages",
    "tag-candidates",
    teamId ?? "unknown",
    query,
  ],
};

export const annotationsKeys = {
  all: () => ["annotations"],
  archived: () => ["annotations", "archived"],
  byReference: (referenceId) => ["annotations", { referenceId }],
  revisionsByReference: (referenceId) => [
    "annotations",
    "revisions",
    { referenceId },
  ],
};

export const pdfImportDecisionKeys = {
  byReference: (referenceId) => ["pdf-import-decisions", { referenceId }],
};

export const citationsKeys = {
  all: () => ["citations"],
  archived: () => ["citations", "archived"],
};

export const brandClaimsKeys = {
  all: () => ["brand-claims"],
  archived: () => ["brand-claims", "archived"],
  detail: (claimId) => ["brand-claims", "detail", claimId],
  annotationIds: (claimId) => ["brand-claims", "annotation-ids", claimId],
  annotationCounts: () => ["brand-claims", "annotation-counts"],
  references: (claimIds = []) => [
    "brand-claims",
    "references",
    claimIds,
  ],
};

export const archiveKeys = {
  summary: () => ["archive", "summary"],
};

export const activityKeys = {
  team: (teamId) => ["activity", "team", teamId],
  changeHistory: ({
    scope,
    scopeId,
    startDate = "",
    endDate = "",
    eventTypes = [],
    userIds = [],
    search = "",
  }) => [
    "activity",
    "change-history",
    scope,
    scopeId,
    {
      startDate,
      endDate,
      eventTypes,
      userIds,
      search,
    },
  ],
  changeHistoryUsers: ({ scope, scopeId }) => [
    "activity",
    "change-history-users",
    scope,
    scopeId,
  ],
};

export const adminKeys = {
  all: () => ["admin"],
  access: () => ["admin", "access"],
  overview: () => ["admin", "overview"],
  impersonation: () => ["admin", "impersonation"],
  requestCounts: () => ["admin", "request-counts"],
  requestQueue: ({ kind = "all", limit = 100 } = {}) => [
    "admin",
    "request-queue",
    { kind, limit },
  ],
  requestDetail: (requestId) => ["admin", "request-detail", requestId],
  search: ({ kind = "all", query = "", limit = 25, offset = 0 } = {}) => [
    "admin",
    "search",
    { kind, query, limit, offset },
  ],
  organization: (orgId) => ["admin", "organization", orgId],
  organizationSecurity: (orgId) => ["admin", "organization-security", orgId],
  user: (userId) => ["admin", "user", userId],
  team: (teamId) => ["admin", "team", teamId],
  caseDetail: (targetType, targetId) => [
    "admin",
    "case-detail",
    targetType ?? "unknown",
    targetId ?? "unknown",
  ],
  contentHealth: () => ["admin", "content-health"],
  billingHealth: () => ["admin", "billing-health"],
  enterpriseContracts: ({ search = "", status = "all", limit = 100 } = {}) => [
    "admin",
    "enterprise-contracts",
    { search, status, limit },
  ],
  enterpriseContract: (contractId) => [
    "admin",
    "enterprise-contract",
    contractId,
  ],
  systemHealth: () => ["admin", "system-health"],
  bugReports: ({ limit = 100, offset = 0 } = {}) => [
    "admin",
    "bug-reports",
    { limit, offset },
  ],
};

export const inviteKeys = {
  public: (token) => ["public-invite", token],
};

export const hierarchyKeys = {
  archiveImpactOverlays: (assetIds) => [
    "hierarchy",
    "archive-impact",
    "overlays",
    assetIds,
  ],
};
